/**
 * Platform Provider
 * 
 * React context that provides platform-specific services to the app.
 * Note: Auth is now handled by AuthProvider - this provides other platform services.
 */

import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { bridge } from '../bridge';
import { getAuthPlatform, type AuthPlatform } from './auth';
import { getPushPlatform, type PushPlatform } from './push';
import { getStoragePlatform, type StoragePlatform } from './storage';
import { getNavigationPlatform, type NavigationPlatform } from './navigation';
import { getHapticsPlatform, type HapticsPlatform } from './haptics';
import { getPurchasePlatform, type PurchasePlatform } from './purchase';
import { useLocation } from 'wouter';

interface PlatformContextValue {
  /** Whether running in native app */
  isNative: boolean;
  
  /** Native platform name (e.g., 'flutter') */
  platform: string | undefined;
  
  /** Auth service (legacy - prefer useAuth hook from AuthProvider) */
  auth: AuthPlatform;
  
  /** Push notifications service */
  push: PushPlatform;
  
  /** Storage service */
  storage: StoragePlatform;
  
  /** Navigation service */
  navigation: NavigationPlatform;
  
  /** Haptics service */
  haptics: HapticsPlatform;

  /** Purchase service (Stripe on web, RevenueCat on native) */
  purchase: PurchasePlatform;
}

const PlatformContext = createContext<PlatformContextValue | null>(null);

interface PlatformProviderProps {
  children: React.ReactNode;
}

export function PlatformProvider({ children }: PlatformProviderProps) {
  const value = useMemo<PlatformContextValue>(() => ({
    isNative: bridge.isNative,
    platform: bridge.platform,
    auth: getAuthPlatform(),
    push: getPushPlatform(),
    storage: getStoragePlatform(),
    navigation: getNavigationPlatform(),
    haptics: getHapticsPlatform(),
    purchase: getPurchasePlatform(),
  }), []);
  const [, setLocation] = useLocation();
  useEffect(() => {
    value.navigation.onDeepLink((path) => {
      console.log('Deep link: ', path);
      setLocation(path);
    });
  }, [value.navigation, setLocation]);
  return (
    <PlatformContext.Provider value={value}>
      {children}
    </PlatformContext.Provider>
  );
}

export function usePlatform(): PlatformContextValue {
  const context = useContext(PlatformContext);
  if (!context) {
    throw new Error('usePlatform must be used within a PlatformProvider');
  }
  return context;
}

// Convenience hooks
export function useIsNative(): boolean {
  return usePlatform().isNative;
}

export function useHaptics(): HapticsPlatform {
  return usePlatform().haptics;
}

export function usePlatformNavigation(): NavigationPlatform {
  return usePlatform().navigation;
}

export function usePurchase(): PurchasePlatform {
  return usePlatform().purchase;
}
